import { Link } from 'react-router';
import { PRIVACY_POLICY_URL, routes } from '@config/routes';
import {
  PoweredByOmneMark,
  RITHMIC_NOTICE,
  TradingPlatformByRithmicMark,
} from '@features/rithmic/components/RithmicAttribution';
import { useRithmicAccountSelected } from '@features/rithmic/hooks/useRithmicAccountSelected';
import logoUrl from '../../../assets/logo.svg';

/**
 * App footer (Figma "Footer", 2026-07): a brand row (the Journal Trading knot + wordmark on the left,
 * Terms/Privacy links on the right) over the required Rithmic/OMNE notices. Full-bleed at the bottom
 * of the app-shell content, theme-aware via tokens. Mounted inside AppShell's scroll area so it sits
 * below the page content.
 *
 * All FOUR notices come from `RITHMIC_NOTICE` (the single source shared with the connect dialogs) —
 * the footer used to keep its own 3-line copy, which is how the "Trading Platform by Rithmic™"
 * trademark notice came to be missing here and how its OMNE line kept reading "OMNE art …".
 *
 * LAYOUT (user direction 2026-07-24): there is no separate badge row any more. Each mark's ARTWORK is
 * inline at the LEFT of its own notice — Rithmic's with the R|Protocol copyright, OMNE's as the
 * subject of the OMNE trademark sentence — so every mark appears exactly once, and the two text-only
 * notices follow. Order below is the requested order. Since 2026-09-30 (user direction) the two
 * text-only notices sit to the RIGHT of the two artwork notices, not beneath them: two columns from
 * `2xl` (1536px) up, where both fit on one line each, stacked below that.
 */

export function Footer() {
  // Rithmic conformance: the OMNE/Rithmic notices show ONLY while a Rithmic account is selected (their
  // artwork must be displayed when their software is in use). Every other account (CQG / imported /
  // manual) gets just the brand row. Shared with the top bar via this hook.
  const isRithmic = useRithmicAccountSelected();

  return (
    // No negative margins any more (2026-08-05): the footer used to bleed through <main>'s p-6, but
    // the padding moved INTO the page wrapper (the sticky-footer restructure), so the footer now
    // sits in unpadded flow and is naturally full-bleed. Its own px-8/py-4 spacing is unchanged.
    <footer className="flex flex-col gap-4 px-8 py-4 text-text">
      {/* Brand row (always shown) */}
      <div className="flex items-center justify-between gap-8">
        <div className="flex items-center gap-2">
          <img src={logoUrl} alt="" aria-hidden className="h-6 w-6" />
          <span className="text-[15px] font-bold leading-6">JournalTrading</span>
        </div>
        <nav className="flex items-center gap-4 text-[15px] font-bold leading-6">
          <Link to={routes.terms} className="transition-opacity hover:opacity-70">Terms &amp; Condition</Link>
          <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" className="transition-opacity hover:opacity-70">Privacy policy</a>
        </nav>
      </div>

      {/* Rithmic-only: the four required notices, each artwork inline with its own notice.
          Hidden entirely for non-Rithmic accounts (their marks show only when in use).
          From `2xl` (1536px) up, a grid filled column by column in two rows: notices 1-2 on the left,
          3-4 to their right (2026-09-30), and the DOM (screen-reader) order stays 1-2-3-4. Narrower,
          each column would wrap every notice onto two lines, so they stay stacked. */}
      {isRithmic && (
        <div className="flex flex-col text-[12px] leading-[22px] tracking-[0.25px] text-text-muted 2xl:grid 2xl:grid-flow-col 2xl:grid-rows-2 2xl:justify-start 2xl:gap-x-10">
          {/* 1 — Rithmic's software copyright, with the Trading-Platform artwork at the left.
                 h-4 against the 22px line-height keeps the mark on the line, and both sources
                 downscale (863×115 / 230×35), so they stay crisp. */}
          <span>
            <TradingPlatformByRithmicMark heightClass="h-4" />
            <span className="ml-1.5">{RITHMIC_NOTICE.apiCopyright}</span>
          </span>
          {/* 2 — OMNE trademark: the ARTWORK is the subject of the sentence, so the text is the
                 tail only ("… is a trademark of …") and never repeats the mark's name. */}
          <span>
            <PoweredByOmneMark heightClass="h-4" />
            <span className="ml-1.5">{RITHMIC_NOTICE.omneTrademarkTail}</span>
          </span>
          {/* 3 + 4 — text-only notices, last. */}
          <span>{RITHMIC_NOTICE.tradingPlatformTrademark}</span>
          <span>{RITHMIC_NOTICE.omneCopyright}</span>
        </div>
      )}
    </footer>
  );
}
